import { useContext, useEffect, useMemo, useRef } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import { useRelease150 } from "../lib/release150";
import { createLiveApplier, isBusy, useLiveConnectionState, useLiveUpdates } from "./useLiveUpdates";
import { nonceOfOverall, reconcileSnapshot } from "./useLiveUpdates.versions";

export { isBusy };

export const DATA_VERSION_POLL_MS = 30_000;

interface DataVersionDto {
  version: string;
  areas: Record<string, number>;
}

/**
 * D-O93 — keeps every open page current without reloading it.
 *
 * Release 1.5.0 on: changes are pushed (useLiveUpdates) and only the pages that read the moved data area refetch. The
 * 30-second check below runs only while the push connection is not open (connecting, reconnecting, blocked or refused),
 * and it too refetches only the areas that moved.
 *
 * Release 1.5.0 off (exactly as 1.4.0): every 30 seconds while the tab is visible (and at once when it becomes visible
 * again) the shell asks the API whether any data changed; only when the answer moves are the page's queries refetched.
 * Failures are ignored; the next check retries.
 */
export function useDataVersionRefresh(enabled: boolean): void {
  // Pure view tests draw the shell without a data layer; then there is nothing to refresh.
  const queryClient = useContext(QueryClientContext);
  const release150 = useRelease150();
  const last = useRef<string | null>(null);

  const applier = useMemo(() => (queryClient && release150 ? createLiveApplier(queryClient) : null), [queryClient, release150]);
  useEffect(() => () => applier?.dispose(), [applier]);
  useLiveUpdates(enabled && release150, applier);
  const live = useLiveConnectionState().status === "live";

  // 1.4.0 behaviour, unchanged, while release 1.5.0 is off.
  useEffect(() => {
    if (release150 || !enabled || !queryClient) return;
    let cancelled = false;

    const check = async () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      try {
        const res = await apiClient.get<DataVersionDto>("/data/version");
        const version = res.data?.version;
        if (cancelled || !version) return;
        if (last.current !== null && last.current !== version) {
          // Never pull the page out from under someone mid-task: while a dialog is open or a field has focus, the
          // refresh waits (the version is not recorded, so the next check tries again).
          if (isBusy()) return;
          void queryClient.invalidateQueries({ refetchType: "active" });
        }
        last.current = version;
      } catch {
        // A failed check changes nothing; the next one retries.
      }
    };

    void check();
    const timer = window.setInterval(() => void check(), DATA_VERSION_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, queryClient, release150]);

  // Release 1.5.0: the fallback check, only while the push connection is not open.
  useEffect(() => {
    if (!release150 || !enabled || !queryClient || !applier || live) return;
    let cancelled = false;

    const check = async () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      try {
        const res = await apiClient.get<DataVersionDto>("/data/version");
        const version = res.data?.version;
        if (cancelled || !version) return;
        applier.apply(reconcileSnapshot(nonceOfOverall(version), res.data.areas ?? {}));
        last.current = version;
      } catch {
        // A failed check changes nothing; the next one retries.
      }
    };

    void check();
    const timer = window.setInterval(() => void check(), DATA_VERSION_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, queryClient, release150, applier, live]);
}
